
//#region Util Imports

import * as _ from 'underscore'

//#endregion

import { CreateEditScope, CreateEditController } from '../../../common/common.create-edit.controller'
import { CreateEditInitParams, BulkEditInitParams } from '../../../common/create-edit-popup/create-edit-popup.controller'

import { CommonHelper } from '../../../../helpers/common-helper'
import { PopupType } from './../../../../helpers/popup-manager'
import { EventsNames, LocalizationHelper } from '../../../../helpers'

import { Enums } from '../../../../models/common'
import { OpOperator, OpDataTypeInGroup } from '../../../../models/core'
import { IOpObject, IReferenceType } from '../../../../models/util/interfaces'

import { OperatorCreateEditService } from '../../../../services/operator'

import { CreateEditSection } from '../../../common/common.create-edit'


interface OperatorCreateEditScope extends CreateEditScope { }

export class OperatorCreateEditController extends CreateEditController {

    //#region Members

    private operator: OpOperator
    private isCopyOperator: boolean
    private actorEditDataTypes: OpDataTypeInGroup[]
    private operatorEditDataTypes: OpDataTypeInGroup[]

    //#endregion


    //#region Ctor

    constructor(
        private $scope: OperatorCreateEditScope,
        private $q: ng.IQService,
        private $timeout: ng.ITimeoutService,
        private operatorCreateEditService: OperatorCreateEditService,
        protected referenceType: Enums.ReferenceType,
        private moduleName: Enums.SimaxModuleName
    ) {
        super(referenceType)

        this.setEvents()
        this.setOptions()
    }

    //#endregion


    //#region Events

    private onInit(args: any[]): void {
        const createEditInitParams: CreateEditInitParams = _.first(args)

        if (createEditInitParams != null) {

            this.isCopyOperator = createEditInitParams.isCopy
            this.operator = createEditInitParams.referenceObject as OpOperator

            $.when(this.getActorSection(), this.getOperatorSection())
                .then((actorSection: CreateEditSection, operatorSection: CreateEditSection) => {
                    this.actorEditDataTypes = actorSection.dataTypesToEdit
                    this.operatorEditDataTypes = operatorSection.dataTypesToEdit

                    createEditInitParams.sections = [
                        actorSection,
                        operatorSection,
                    ]

                    this.$scope.$broadcast(EventsNames.SHOW_CREATE_EDIT_POPUP, PopupType.CREATE_EDIT_POPUP)
                    this.$scope.options.title = this.getPopupTitle()

                    createEditInitParams.referenceObject = this.createIOpObject()

                    this.$timeout(() => {
                        this.$scope.$broadcast(EventsNames.OPEN_CREATE_EDIT_POPUP, createEditInitParams)
                    })
                })
        }
    }

    private onInitBulk(args: any[]): void {
        const bulkEditInitParams: BulkEditInitParams = _.first(args)

        if (bulkEditInitParams != null) {

            this.$scope.$broadcast(EventsNames.SHOW_CREATE_EDIT_POPUP, PopupType.BULK_EDIT_POPUP)
            this.$scope.options.title = LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__MASS_EDITING, this.moduleName)

            this.$scope.gridData = bulkEditInitParams

            if (args[1] != null) {
                this.$scope.gridColumns = args[1]
            }

            this.$scope.$broadcast(EventsNames.OPEN_BULK_EDIT_POPUP, bulkEditInitParams)
        }
    }

    private onSave(objectToSave: IOpObject): ng.IPromise<IOpObject> {
        const deferred = this.$q.defer<IOpObject>()
        const operatorToSave: OpOperator = this.isCopyOperator
            ? this.operatorCreateEditService.createCopyOperatorFromIOpObject(this.operator, objectToSave, this.operatorEditDataTypes, this.actorEditDataTypes)
            : this.operatorCreateEditService.createOperatorFromIOpObject(objectToSave, this.operatorEditDataTypes, this.actorEditDataTypes)

        this.operatorCreateEditService
            .saveOperator(operatorToSave)
            .then((savedOperator: OpOperator) => {
                this.$scope.$root.$broadcast(EventsNames.CLOSE_CREATE_EDIT_POPUP, savedOperator)

                deferred.resolve(savedOperator)
            })
            .fail((error: any) => {
                deferred.reject(error)
            })

        return deferred.promise
    }

    private onBulkSave(objectToSave: IOpObject): ng.IPromise<IOpObject> {
        const deferred = this.$q.defer<IOpObject>()
        const operator: OpOperator = this.operatorCreateEditService.createBulkOperatorFromIOpObject(objectToSave);

        this.operatorCreateEditService
            .saveOperator(operator, true)
            .then((savedObject: OpOperator) => {
                deferred.resolve(savedObject)
            })
            .fail((error: any) => {
                deferred.reject(error)
            })

        return deferred.promise
    }

    //#endregion


    //#region Setup

    private createIOpObject(): IReferenceType & IOpObject {
        if (this.isCopyOperator) {
            return this.operatorCreateEditService.createCopyIOpObjectFromOperator(this.operator, this.operatorEditDataTypes, this.actorEditDataTypes)
        }

        return this.operatorCreateEditService.createIOpObjectFromOperator(this.operator, this.operatorEditDataTypes, this.actorEditDataTypes)
    }

    private getOperatorSection(): JQueryPromise<CreateEditSection> {
        return this.operatorCreateEditService
            .getOperatorEditDataTypeGroup()
            .then((panelParamsToEdit: OpDataTypeInGroup[], paramsRequired: OpDataTypeInGroup[]) => {
                return {
                    name: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Operator),
                    type: "form",
                    dataTypesToEdit: panelParamsToEdit,
                    dataTypesRequired: paramsRequired
                }
            })
    }

    private getActorSection(): JQueryPromise<CreateEditSection> {
        return this.operatorCreateEditService
            .getActorEditDataTypeGroup()
            .then((panelParamsToEdit: OpDataTypeInGroup[], paramsRequired: OpDataTypeInGroup[]) => {
                return {
                    name: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.PersonalDetails),
                    type: "form",
                    dataTypesToEdit: panelParamsToEdit,
                    dataTypesRequired: paramsRequired
                }
            })
    }

    private getPopupTitle(): string {
        if (this.operator != null && this.isCopyOperator) {
            return LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__COPYING, this.moduleName)
        }

        if (this.operator != null) {
            return LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__EDITING, this.moduleName)
        }

        return LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__ADDING, this.moduleName)
    }

    private setEvents(): void {
        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__OPEN_CREATE_EDIT_POPUP, this.moduleName),
            (event: ng.IAngularEvent, ...args: any[]) => this.onInit(args)
        )

        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__OPEN_BULK_EDIT_POPUP, this.moduleName),
            (event: ng.IAngularEvent, ...args: any[]) => this.onInitBulk(args)
        )
    }

    private setOptions(): void {
        this.$scope.popupVisible = false
        this.$scope.options = {
            title: '',
            type: PopupType.USER_POPUP,
            referenceType: this.referenceType,
            onSave: (objectToSave: IOpObject) => this.onSave(objectToSave),
            onBulkSave: (objectToSave: IOpObject) => this.onBulkSave(objectToSave),
            onSubmit: null,
            onClose: null,
            submitBulkEdit: null,
            localStorageVarForSavedEntity: CommonHelper.localStorageVarForSavedEntity.userSaved,
            succsessMessage: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVED, this.moduleName),
        }
    }

    //#endregion
}